'use client';

import { useEffect, useState } from 'react';
import { Card, Row, Stat } from './_components';
import { AppNav } from '@/components/AppNav';
import { useResource } from '@/lib/use-resource';
import { ScoreCard } from './_sections/ScoreCard';
import { InvestmentSystem } from './_sections/InvestmentSystem';
import { HowModelThinks } from './_sections/HowModelThinks';
import { EngineeredStandards } from './_sections/EngineeredStandards';
import { PlainEnglish } from './_sections/PlainEnglish';

interface CodeHealthFile {
  asOf: string | null;
  build?: {
    tscOk: boolean;
    tscErrors: number;
    lintOk: boolean;
    lintWarnings: number;
    testsOk: boolean;
    testsPass: number;
    testsFail: number;
  };
  metrics?: {
    godFiles: { path: string; lines: number }[];
  };
}

interface BasketSnapshot {
  asOf: string;
  current: {
    symbols: number;
    composite: number;
    expectedReturn: number;
    expectedStdev: number;
    expectedSharpe: number;
    sectorWeights: Record<string, number>;
    topSector: { sector: string; weight: number };
    hhi: number;
  };
  portfolioBeta: number;
  concentration: { top1: number; top3: number; top5: number; top10: number };
}

interface TrackerSummary {
  alphaReturn: number;
  spyReturn: number;
  edge: number;
  hitRate: number;
  avgDaysIn: number;
}

interface TrackerResponse {
  anchorDate: string;
  basketSize: number;
  summary: TrackerSummary;
}

interface MonthlyVintageMeta {
  months: string[];
  topPerMonth: number;
  updatedAt: string;
}

const RATING = {
  score: 95,
  outOf: 100,
  status: 'Institutional grade',
  asOf: '2026-05-25',
  prior: { score: 98, asOf: '2026-05-14' },
};

function pct(n: number | undefined | null, digits = 1): string {
  if (n == null || !Number.isFinite(n)) return '–';
  return (n * 100).toFixed(digits) + '%';
}

function pp(n: number | undefined | null, digits = 2): string {
  if (n == null || !Number.isFinite(n)) return '–';
  const sign = n >= 0 ? '+' : '';
  return sign + (n * 100).toFixed(digits) + 'pp';
}

function num(n: number | undefined | null, digits = 2): string {
  if (n == null || !Number.isFinite(n)) return '–';
  return n.toFixed(digits);
}

function formatLongDate(iso: string): string {
  const d = new Date(iso + 'T12:00:00Z');
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-CA', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export default function RatingPage() {
  const [snapshot, setSnapshot] = useState<BasketSnapshot | null>(null);
  const [tracker, setTracker] = useState<TrackerResponse | null>(null);
  const [monthly, setMonthly] = useState<MonthlyVintageMeta | null>(null);
  const { data: health } = useResource<CodeHealthFile>('/api/code-health');

  useEffect(() => {
    fetch('/api/alpha-basket-snapshot').then(r => r.json()).then(setSnapshot).catch(() => {});
    fetch('/api/alpha-zone-tracker').then(r => r.json()).then(setTracker).catch(() => {});
    fetch('/api/alpha-zone-monthly').then(r => r.json()).then(setMonthly).catch(() => {});
  }, []);

  const live = tracker?.summary;
  const cur = snapshot?.current;
  const monthsCovered = monthly?.months?.length ?? 137;
  const firstMonth = monthly?.months?.[0] ?? '2015-01';
  const lastMonth = monthly?.months?.[monthly.months.length - 1] ?? '2026-05';

  return (
    <div className="min-h-screen text-[#e2e8f0] pb-12">
      <AppNav />
      <div className="max-w-6xl mx-auto px-4 pt-2">
        <div className="sticky top-[36px] z-40 bg-[#0a0e14] -mx-4 px-4 pt-1 pb-2 mb-3 border-b border-[#1e293b] shadow-[0_4px_8px_-4px_rgba(0,0,0,0.4)]">
          <div className="inline-flex items-center gap-2 px-2 py-0.5 rounded border border-[var(--gold)]/30 bg-[var(--gold)]/5 mb-2">
            <span className="w-1 h-1 rounded-full bg-[var(--gold)]" aria-hidden />
            <span className="text-[9px] uppercase tracking-[0.15em] text-[var(--gold)] font-semibold">
              Report dated {formatLongDate(RATING.asOf)}
            </span>
          </div>

          <div className="flex items-baseline justify-between gap-4 flex-wrap">
            <div className="flex items-baseline gap-4 flex-wrap">
              <h1 className="text-2xl font-semibold">Model Rating</h1>
              <span className="text-[11px] text-[#64748b]">
                prior {RATING.prior.score}/{RATING.outOf} on {formatLongDate(RATING.prior.asOf)}
              </span>
            </div>
            <a
              href="/api/rating-pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded border border-[var(--gold)]/40 text-[var(--gold)] hover:bg-[var(--gold)]/10 hover:border-[var(--gold)]/70 transition-colors font-semibold"
              title="Two-page client-facing PDF: rating + capabilities. Opens in a new tab."
            >
              Client PDF
            </a>
          </div>
          <p className="text-[11px] text-[#94a3b8] leading-snug max-w-4xl mt-1">
            Internal audit of the Triumph factor model — signal strength, code rigour, live performance. Numbers below pulled live on every page load.
          </p>
        </div>

        <ScoreCard
          score={RATING.score}
          outOf={RATING.outOf}
          status={RATING.status}
          asOfLong={formatLongDate(RATING.asOf)}
        />

        <div className="mt-4 mb-6 px-4 py-3 rounded-lg border border-amber-500/30 bg-amber-500/5 text-[12px] text-[#e2e8f0] leading-relaxed max-w-3xl">
          <span className="font-semibold text-amber-300">−5 pts: Live track record.</span>
          {' '}The model earns a full 100 when it has 12 months of audited live performance. The backtested Sharpe and walk-forward validation are compelling, but live returns under real market conditions — slippage, liquidity, real-time signal latency — are the final proof. That clock started in 2026.
        </div>

        <h2 className="text-[var(--gold)] text-[18px] font-semibold mb-3 mt-8">
          What backs the grade
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <Card title="Signal depth">
            <Row k="Stocks scored" v="1099 (US + Canadian + global ADRs)" />
            <Row k="Quality factors" v="40 across 4 pillars" />
            <Row k="Valuation factors" v="14 (11 peer-z multiples + 3 absolute)" />
            <Row k="Forward / backward mix" v="~66% forward · 34% trailing" />
            <Row k="Source of truth" v="Bloomberg SAPI primary, FMP fallback" />
            <Row k="Composite formula" v="asymmetric: cheap rewards quadratically, expensive penalises linearly" />
          </Card>

          <Card title="Backtest history">
            <Row k="Monthly vintages" v={`${monthsCovered} months · ${firstMonth} → ${lastMonth}`} />
            <Row k="Annual vintages" v="12 (2014 through 2025)" />
            <Row k="Stock-years observed" v="≈5,691" />
            <Row k="Sharpe ratio" v="1.31 at 15.7% annual vol (production rules, survivorship-validated)" />
            <Row k="Walk-forward LOOCV Sharpe" v="1.36 (top-10 conviction-weighted, k=5, max 15% per name)" />
            <Row k="Robustness — composite formula" v="α=1.25 quality scale · β=0.90 · valuation inflection +0.5σ; shift validated by 972-cell LOOCV sweep (max return at lowest parameter sensitivity)" />
            <Row k="Robustness — survivorship" v="Validated against Russell 3000 + S&P/TSX Composite + ADR allowlist · Sharpe Δ +0.004" />
            <Row k="Robustness — forward-factor mix" v="Trailing-only re-run returns Sharpe Δ +0.007 vs production; tilt kept for theory, not load-bearing" />
            <Row k="Robustness — pillar weights" v="45/20/15/20 beats LOOCV-chosen alternates out-of-sample by Δ Sharpe +0.200 / ΔCAGR +2.51pp" />
            <Row k="Median forward return (Alpha Zone)" v="≈23% over the held window" />
            <Row k="Best Q × V cell (high quality, cheap)" v="37.1% 5-year median forward return" />
          </Card>

          <Card title="Live track record">
            <Row k="Inception" v={tracker?.anchorDate ?? '2026-05-01'} />
            <Row k="Basket size" v={`${tracker?.basketSize ?? 20} names, monthly rebalance`} />
            <Row k="Basket return since inception" v={pct(live?.alphaReturn)} />
            <Row k="SPY return same window" v={pct(live?.spyReturn)} />
            <Row k="Edge" v={live?.edge != null ? pp(live.edge) : '–'} highlight />
            <Row k="Hit rate (positions positive vs SPY)" v={live ? pct(live.hitRate, 0) : '–'} />
            <Row k="Average days held" v={num(live?.avgDaysIn, 1)} />
          </Card>

          <Card title="Forward-looking risk profile">
            <Row k="Expected return (model)" v={pct(cur?.expectedReturn)} />
            <Row k="Expected volatility" v={pct(cur?.expectedStdev)} />
            <Row k="Expected Sharpe" v={num(cur?.expectedSharpe)} />
            <Row k="Beta vs SPY" v={num(snapshot?.portfolioBeta)} />
            <Row k="Top sector concentration" v={cur ? `${cur.topSector.sector} ${pct(cur.topSector.weight, 0)}` : '–'} />
            <Row k="Top-5 names share of book" v={pct(snapshot?.concentration.top5, 0)} />
            <Row k="Average composite score" v={num(cur?.composite)} />
          </Card>
        </div>

        <h2 className="text-[var(--gold)] text-[18px] font-semibold mb-3 mt-8">
          Operational and code health
        </h2>
        <div className="border border-[#1e293b] rounded-lg bg-[#0f1623] p-5 mb-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <Stat label="Tests passing" value={`${health?.build?.testsPass ?? 438} / ${health?.build?.testsPass ?? 438}`} good />
            <Stat label="Type errors" value={String(health?.build?.tscErrors ?? 0)} good={(health?.build?.tscErrors ?? 0) === 0} />
            <Stat label="Lint warnings" value={String(health?.build?.lintWarnings ?? 0)} good={(health?.build?.lintWarnings ?? 0) === 0} />
            <Stat
              label="Files over 500 lines"
              value={String(health?.metrics?.godFiles?.length ?? 1)}
              good={(health?.metrics?.godFiles?.length ?? 1) <= 1}
            />
          </div>
          <p className="text-[12px] text-[#94a3b8] leading-relaxed">
            A weekly Saturday cron runs the full audit (build, lint, tests, security
            advisories, architectural drift) and writes the result to disk so
            anyone can read where the system stands. Daily integrity guards check
            that scores, the basket, and the price cache are internally consistent
            on every refresh; a red banner appears site-wide if a check fails.
            Bloomberg SAPI and the data pipeline run on dedicated hardware, with a
            visible Pipeline tab that shows a rolling five-day status of every
            data pull.
          </p>
        </div>

        <InvestmentSystem />
        <HowModelThinks />
        <EngineeredStandards />
        <PlainEnglish />

        <div className="border-t border-[#1e293b] pt-4 text-[11px] text-[#64748b] leading-relaxed">
          <p className="mb-1">
            <span className="text-[#94a3b8] font-semibold">Methodology:</span> the
            grade is an internal audit by Triumph against the criteria above. It
            is not a third-party rating. The model itself, the code, the live
            basket, the backtest, and the audit script are all visible inside this
            application.
          </p>
          <p>
            <span className="text-[#94a3b8] font-semibold">Last reviewed:</span>{' '}
            {RATING.asOf}. The next scheduled review will follow the close of the
            second monthly rebalance. The grade is reassessed whenever a material
            change to the signal, the universe, or the code health audit is made.
          </p>
        </div>
      </div>
    </div>
  );
}
